import React, { useEffect, useState, useContext, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Grid,
  Select,
  MenuItem,
  Chip,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import AddIcon from "@mui/icons-material/Add";
import PaymentIcon from "@mui/icons-material/Payment";
import Footer from "../../Layout/Footer/footer";
import NavigationBar from "../../Layout/Header/header";
import axios from "axios";
import { API_CONFIG } from "../../api/apiUrl";
import { useParams, useLocation } from "react-router-dom";
import { AuthContext } from "../../context/authcontext";
import toast, { Toaster } from "react-hot-toast";
import {
  ConstructionStage,
  ConstructionStageNotMapped,
} from "../../types/Milestone";

const MileStone = () => {
  const [constructionStages, setConstructionStages] = useState<
    ConstructionStage[]
  >([]);
  const [constructionStagesNotMapped, setConstructionStagesNotMapped] =
    useState<ConstructionStageNotMapped[]>([]);
  const [companyName, setCompanyName] = useState("Unknown Company");
  const [loading, setLoading] = useState(false);
  const [savingStageId, setSavingStageId] = useState<number | null>(null);
  const [approvingPaymentId, setApprovingPaymentId] = useState<number | null>(null);


  const token = localStorage.getItem("id_token");
  const { id } = useParams<{ id: string }>();
  const { search } = useLocation();
  const {
    user,
    plots,
    loading: authLoading,
    selectedPlotId,
    authorities,
  } = useContext(AuthContext)!;
  const selectedPlotIdFinal = new URLSearchParams(search).get("plotId")
    ? Number(new URLSearchParams(search).get("plotId"))
    : selectedPlotId;

  const fetchCompanyProfile = async () => {
    if (!id) {
      toast.error("Company ID missing");
      return;
    }
    try {
      const { data } = await axios.get(API_CONFIG.COMPANY_PROFILE_BY_ID(id), {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      setCompanyName(data.name || "Unknown Company");
    } catch (error) {
      toast.error("Failed to fetch company profile");
    }
  };

  const fetchConstructionStages = async () => {
    if (!id || !selectedPlotIdFinal) {
      toast.error("Missing company or plot ID");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(
        API_CONFIG.COMPANY_PLOT_CONSTRUCTION_STAGES(
          parseInt(id),
          selectedPlotIdFinal,
          0,
          10
        ),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      // console.log("[GET] Construction stages response:", JSON.stringify(data, null, 2));
      setConstructionStages(
        (data.constructionStages || []).map((stage: ConstructionStage) => ({
          ...stage,
          status: allowedStatuses.includes(stage.remarks || "")
            ? stage.remarks
            : "",
        }))
      );
      setConstructionStagesNotMapped(
        (data.constructionStagesNotMapped || []).map(
          (stage: ConstructionStageNotMapped) => ({
            ...stage,
            status: allowedStatuses.includes(stage.remarks || "")
              ? stage.remarks
              : "",
          })
        )
      );
    } catch (error) {
      toast.error("Failed to fetch stages");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id && selectedPlotIdFinal) {
      fetchCompanyProfile();
      fetchConstructionStages();
    } else {
      toast.error("Required parameters missing");
    }
  }, [id, selectedPlotIdFinal]);

  const handleSave = async (
    stage: ConstructionStage | ConstructionStageNotMapped
  ) => {
    if (!id || !user || !selectedPlotIdFinal) {
      toast.error("Invalid data");
      return;
    }
    setSavingStageId(stage.id); 
    const stageName =
      "constructionStages" in stage
        ? stage.constructionStages?.name || "Booking"
        : stage.name || "Booking";
    const stageId =
      "constructionStages" in stage ? stage.constructionStages?.id : stage.id;
    const postData = {
      constructionStages: [
        {
          id: stage.id,
          name: stageName,
          remarks: stage.status || stage.remarks || "",
          createdDate: new Date().toISOString(),
          updatedDate: new Date().toISOString(),
          isPaymentLinked: stage.isPaymentLinked,
          paymentPercent: stage.isPaymentLinked
            ? stage.paymentPercent || stage.percent || 0
            : 0,
          amount: stage.amount || 0,
          startDate: stage.startDate,
          endDate: stage.endDate,

          ...(stageName && stageName.toLowerCase() === "booking" && { paymentDate: stage.paymentDate }),
          isPaid: false, 
          isPaymentRequested: false, 
          user: {
            id: user.id,
            login: user.login,
            firstName: user.firstName || "Unknown",
          },
          companyProfile: { id: parseInt(id) },
          plot: { id: selectedPlotIdFinal },
          constructionStages: { id: stageId },
        },
      ],
    };
    // console.log("[POST] Saving stage payload:", JSON.stringify(postData, null, 2));
    try {
      await axios.post(API_CONFIG.COMPANY_PROFILE_RATES_POST, postData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      toast.success("Stage saved successfully");
      await fetchConstructionStages(); 
    } catch (error) {
      toast.error("Failed to save stage");
    } finally {
      setSavingStageId(null);
    }
  };

  const handleRequestPayment = async (
    stage: ConstructionStage | ConstructionStageNotMapped
  ) => {
    if (!id || !user || !selectedPlotIdFinal) {
      toast.error("Invalid data");
      return;
    }
    
    try {
      const stageId =  "constructionStages" in stage ? stage.constructionStages?.id : stage.id; 
      const stageName =
        "constructionStages" in stage
          ? stage.constructionStages?.name || "Booking"
          : stage.name || "Booking";
      

      const updateData = {
        id: stage.id,
        startDate: stage.startDate,
        endDate: stage.endDate,
        remarks: stage.status || stage.remarks || "",
        isPaymentLinked: stage.isPaymentLinked || false,
        paymentPercent: stage.isPaymentLinked
          ? stage.paymentPercent || stage.percent || 0
          : 0,
        amount: stage.amount || 0,
        isPaid: stage.isPaid || false,
        isPaymentRequested: true, 
        isNext: stage.isNext || false,
        user: {
          id: user.id,
          login: user.login,
        },
        companyProfile: { 
          id: parseInt(id),
        },
        plot: { 
          id: selectedPlotIdFinal,
        },
        constructionStages: { 
          id: stageId,
        }
      };
      // console.log("[PUT] Payment request payload:", JSON.stringify(updateData, null, 2));
      await axios.put(`${API_CONFIG.BASE_URL}restapi/company-plot-construction-stages/${stage.id}`, updateData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }); 
      toast.success("Payment request sent successfully");
      fetchConstructionStages();
    } catch (error) {
      toast.error("Failed to request payment");
    }
  };

  const handleApprovePayment = async (
    stage: ConstructionStage | ConstructionStageNotMapped
  ) => {
    if (!id || !user || !selectedPlotIdFinal) {
      toast.error("Invalid data");
      return;
    }
    
    setApprovingPaymentId(stage.id);
    try {
      const paymentId = (stage as any).paymentId; 
      // console.log(paymentId,"fff")
      if (!paymentId) {
        toast.error("No payment ID found for this stage");
        return;
      }
    //  console.log("[PUT] Payment approval for payment ID:", paymentId);
      
      const requestBody = {
        id: paymentId,
        status: "APPROVED"
      };
      
      await axios.patch(API_CONFIG.PAYMENT_APPROVE(paymentId.toString()), requestBody, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      }); 
      toast.success("Payment approved successfully");
      fetchConstructionStages();
    } catch (error) {
      toast.error("Failed to approve payment");
    } finally {
      setApprovingPaymentId(null);
    }
  };

  const handleDateChange = (
    stage: ConstructionStage | ConstructionStageNotMapped,
    field: "startDate" | "endDate" | "paymentDate",
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const updatedStage = { ...stage, [field]: event.target.value || undefined };
    const isMapped = "constructionStages" in stage;
    if (isMapped) {
      const index = constructionStages.findIndex((s) => s.id === stage.id);
      if (index !== -1) {
        setConstructionStages((prev: ConstructionStage[]) => [
          ...prev.slice(0, index),
          updatedStage as ConstructionStage,
          ...prev.slice(index + 1),
        ]);
      }
    } else {
      const index = constructionStagesNotMapped.findIndex(
        (s) => s.id === stage.id
      );
      if (index !== -1) {
        setConstructionStagesNotMapped((prev: ConstructionStageNotMapped[]) => [
          ...prev.slice(0, index),
          updatedStage as ConstructionStageNotMapped,
          ...prev.slice(index + 1),
        ]);
      }
    }
  };

  const handleStatusChange = (
    stage: ConstructionStage | ConstructionStageNotMapped,
    value: string
  ) => {
    const updatedStage = { ...stage, status: value, remarks: value };
    const isMapped = "constructionStages" in stage;
    if (isMapped) {
      const index = constructionStages.findIndex((s) => s.id === stage.id);
      if (index !== -1) {
        setConstructionStages((prev: ConstructionStage[]) => [
          ...prev.slice(0, index),
          updatedStage as ConstructionStage,
          ...prev.slice(index + 1),
        ]);
      }
    } else {
      const index = constructionStagesNotMapped.findIndex(
        (s) => s.id === stage.id
      );
      if (index !== -1) {
        setConstructionStagesNotMapped((prev: ConstructionStageNotMapped[]) => [
          ...prev.slice(0, index),
          updatedStage as ConstructionStageNotMapped,
          ...prev.slice(index + 1),
        ]);
      }
    }
  };





  const today = new Date().toISOString().split("T")[0];

  const allowedStatuses = ["Pending", "In Process", "Complete", "On Hold"];
  const isStageValid = (stage: {
    startDate?: string;
    endDate?: string;
    status?: string;
  }) => {
    return Boolean(
      stage.startDate &&
        stage.endDate &&
        allowedStatuses.includes(stage.status || "")
    );
  };

  return (
    <Box sx={{ maxWidth: "2000px", margin: "0 auto" }}>
      <NavigationBar />
      <Toaster position="top-right" />
      <Box
        sx={{
          py: { xs: 2, sm: 3, md: 3, lg: 3, xl: 3 },
          px: { xs: 1, sm: 0.8, md: 2, lg: 4, xl: 6 },
        }}
      >
        <Box sx={styles.mainContainer}>
          <Box sx={styles.header}>
            <Typography
              sx={{
                fontSize: { xs: "20px", sm: "25px", md: "25px" },
                fontWeight: 500,
                lineHeight: { xs: 1.4, sm: 1.5, md: 1.6 },
              }}
            >
              Milestone
            </Typography>
          </Box>
          <Box sx={styles.mainBox}>
            <Box sx={styles.borderBox}>
              <Box sx={{ padding: { xs: 0.5, sm: 2, md: 1 } }}>
                <Box sx={{ maxWidth: "700px" }}>
                  <Box
                    sx={{
                      pt: { xs: 1, md: 2.7 },
                      px: { xs: 2, sm: 1, md: 2, lg: 7, xl: 10 },
                    }}
                  >
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography sx={styles.fonstyle}>
                          Customer Name
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography sx={styles.fonstyle}>
                          {constructionStages[0]?.plot?.plotOwner?.user
                            ?.firstName || ""}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography sx={styles.fonstyle}>
                          Plot Location
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography sx={styles.fonstyle}>
                          {constructionStages[0]?.plot?.plotLocation || " "}
                        </Typography>
                      </Grid>

                    </Grid>
                  </Box>
                </Box>
                <Box sx={{ maxWidth: "1000px" }}>
                  <Box
                    sx={{
                      pt: { xs: 1, md: 1 },
                      px: { xs: 1.4, sm: 1, md: 2, lg: 7, xl: 10 },
                    }}
                  >
                    {loading || authLoading ? (
                      <></>
                    ) : constructionStages.length > 0 ||
                      constructionStagesNotMapped.length > 0 ? (
                      [
                        ...constructionStages,
                        ...constructionStagesNotMapped.filter(
                          (stage) => !stage.isDeleted
                        ),
                      ]
                        .sort((a, b) => {
                          const orderA =
                            ("constructionStages" in a
                              ? a.constructionStages?.id
                              : a.diplayOrder) || 0;
                          const orderB =
                            ("constructionStages" in b
                              ? b.constructionStages?.id
                              : b.diplayOrder) || 0;
                          return orderA - orderB;
                        })
                        .map((stage, index) => {
                          const minEndDate = stage.startDate
                            ? new Date(
                                new Date(stage.startDate).getTime() +
                                  24 * 60 * 60 * 1000
                              )
                                .toISOString()
                                .split("T")[0]
                            : today;

                          const stageName =
                            "constructionStages" in stage && stage.constructionStages?.name
                              ? stage.constructionStages.name
                              : stage.name;
                          const isBooking = stageName && stageName.toLowerCase() === "booking";
                          const isPaid = !!stage.isPaid;
                          const isComplete = stage.remarks === "Complete" || stage.status === "Complete";

                          return (
                            <Grid
                              container
                              alignItems="center"
                              spacing={1}
                              sx={{ mb: 2 }}
                              key={index}
                            >
                              <Grid item xs={12} sm={4}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <Typography sx={styles.fonstyle}>
                                    {stageName}
                                  </Typography>
                                  {/* {stage.isNext && (
                                    <Chip
                                      label="Next"
                                      size="small"
                                      color="primary"
                                      sx={{ fontSize: '10px', height: '20px' }}
                                    />
                                  )} */}
                                  {/* {stage.isPaid && (
                                    <Chip
                                      label="Paid"
                                      size="small"
                                      color="success"
                                      sx={{ fontSize: '10px', height: '20px' }}
                                    />
                                  )} */}
                                </Box>
                              </Grid>
                            
                              <Grid item xs={6} sm={2.5} md={2.5}>
                                {isBooking ? (
                                  (isPaid || isComplete) ? (
                                    <input
                                      type="date"
                                      value={stage.paymentDate ? stage.paymentDate.slice(0, 10) : ''}
                                      disabled
                                      style={{
                                        ...styles.dateInput,
                                        color: "rgba(0, 0, 0, 0.38)",
                                        borderColor: "rgba(0, 0, 0, 0.12)",
                                        cursor: "not-allowed",
                                      }}
                                    />
                                  ) : (
                                    <input
                                      type="date"
                                      value={stage.paymentDate ? stage.paymentDate.slice(0, 10) : ''}
                                      onChange={e => handleDateChange(stage, "paymentDate", e)}
                                      min={today}
                                      style={styles.dateInput}
                                    />
                                  )
                                ) : (
                                  (isPaid || isComplete) ? (
                                    <input
                                      type="date"
                                      value={stage.startDate || ""}
                                      disabled
                                      style={{
                                        ...styles.dateInput,
                                        color: "rgba(0, 0, 0, 0.38)",
                                        borderColor: "rgba(0, 0, 0, 0.12)",
                                        cursor: "not-allowed",
                                      }}
                                    />
                                  ) : (
                                    <input
                                      type="date"
                                      value={stage.startDate || ""}
                                      onChange={(e) => handleDateChange(stage, "startDate", e)}
                                      min={today}
                                      style={styles.dateInput}
                                    />
                                  )
                                )}
                              </Grid>
                    
                              <Grid item xs={6} sm={2.5} md={2.5}>
                                {isBooking ? (
                                  (isPaid || isComplete) ? (
                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pl: 1 }}>
                                      <Chip
                                        label="Paid"
                                        color="success"
                                        icon={<CheckCircleIcon />}
                                        sx={{ fontWeight: 600, fontSize: 14, bgcolor: '#43a047', color: 'white', px: 1.5 }}
                                      />
                                    </Box>
                                  ) : (
                                    <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', pl: 1 }}>
                                      <Typography sx={{ fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
                                        N/A
                                      </Typography>
                                    </Box>
                                  )
                                ) : (isPaid || isComplete) ? (
                                  <input
                                    type="date"
                                    value={stage.endDate || ""}
                                    disabled
                                    style={{
                                      ...styles.dateInput,
                                      color: "rgba(0, 0, 0, 0.38)",
                                      borderColor: "rgba(0, 0, 0, 0.12)",
                                      cursor: "not-allowed",
                                    }}
                                  />
                                ) : (
                                  <input
                                    type="date"
                                    value={stage.endDate || ""}
                                    onChange={(e) => handleDateChange(stage, "endDate", e)}
                                    min={minEndDate}
                                    style={styles.dateInput}
                                  />
                                )}
                              </Grid>
                              <Grid item xs={6} sm={2} md={2}>
                                {isBooking ? null : (isPaid || isComplete) ? (
                                  <Select
                                    fullWidth
                                    size="small"
                                    value={
                                      allowedStatuses.includes(stage.status || "")
                                        ? stage.status || ""
                                        : ""
                                    }
                                    disabled
                                    sx={styles.select}
                                  >
                                    <MenuItem value="" disabled>
                                      Status
                                    </MenuItem>
                                    {allowedStatuses.map((status) => (
                                      <MenuItem key={status} value={status}>
                                        {status}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                ) : (
                                  <Select
                                    fullWidth
                                    size="small"
                                    value={
                                      allowedStatuses.includes(stage.status || "")
                                        ? stage.status || ""
                                        : ""
                                    }
                                    onChange={(e) =>
                                      handleStatusChange(stage, e.target.value)
                                    }
                                    sx={styles.select}
                                  >
                                    <MenuItem value="" disabled>
                                      Status
                                    </MenuItem>
                                    {allowedStatuses.map((status) => (
                                      <MenuItem key={status} value={status}>
                                        {status}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                )}
                              </Grid>
                              <Grid item xs={6} sm={1} md={1} lg={1}>
                                <Box sx={styles.buttonContainer}>
                                  {isBooking ? null : (
                                    <>
                                      <Tooltip title="Save Stages" arrow placement="top">
                                        <Button
                                          size="small"
                                          sx={styles.actionButton}
                                          onClick={() => handleSave(stage)}
                                          disabled={savingStageId === stage.id || !isStageValid(stage) || !stage.isNext || stage.isPaid}
                                        >
                                          {savingStageId === stage.id ? <CircularProgress size={16} /> : <AddIcon />}
                                        </Button>
                                      </Tooltip>
                                      {isPaid ? (
                                        <Tooltip title="Paid" arrow placement="top">
                                          <Button
                                            size="small"
                                            sx={{
                                              ...styles.actionButton,
                                              bgcolor: '#43a047',
                                              '&:hover': { bgcolor: '#2e7d32' }
                                            }}
                                            disabled
                                          >
                                            <CheckCircleIcon sx={{ color: 'white' }} />
                                          </Button>
                                        </Tooltip>
                                      ) : (
                                        <Tooltip title="Request Payment" arrow placement="top">
                                          <Button
                                            size="small"
                                            sx={styles.actionButton}
                                            onClick={() => handleRequestPayment(stage)}
                                            disabled={savingStageId === stage.id || !stage.isNext || stage.isPaid || stage.isPaymentRequested}
                                          >
                                            <PaymentIcon />
                                          </Button>
                                        </Tooltip>
                                      )}
                                      {authorities.includes("ROLE_RM") && (
                                        (stage as any).paymentStatus === "APPROVED" ? (
                                          <Tooltip title="Payment Approved" arrow placement="top">
                                            <Button
                                              size="small"
                                              sx={{
                                                ...styles.actionButton,
                                                bgcolor: '#43a047',
                                                '&:hover': { bgcolor: '#2e7d32' }
                                              }}
                                              disabled
                                            >
                                              <CheckCircleIcon sx={{ color: 'white' }} />
                                            </Button>
                                          </Tooltip>
                                        ) : (
                                          <Tooltip title="Approve Payment" arrow placement="top">
                                            <Button
                                              size="small"
                                              sx={{
                                                ...styles.actionButton,
                                                bgcolor: '#2196f3',
                                                '&:hover': { bgcolor: '#1976d2' }
                                              }}
                                              onClick={() => handleApprovePayment(stage)}
                                              disabled={approvingPaymentId === stage.id || !stage.isPaymentRequested || !(stage as any).paymentId}
                                            >
                                              {approvingPaymentId === stage.id ? (
                                                <CircularProgress size={16} sx={{ color: 'white' }} />
                                              ) : (
                                                <CheckCircleIcon sx={{ color: 'white' }} />
                                              )}
                                            </Button>
                                          </Tooltip>
                                        )
                                      )}
                                    </>
                                  )}
                                </Box>
                              </Grid>
                            </Grid>
                          );
                        })
                    ) : (
                      <Typography>No construction stages available.</Typography>
                    )}
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>



      <Footer />
    </Box>
  );
};

export default MileStone;

const styles: any = {
  mainContainer: {
    borderRadius: { xs: "10px", sm: "10px" },
    boxShadow: "0px 0px 3px 0px #00000033",
  },
  header: {
    background: "linear-gradient(90deg, #89652A 0%, #B69D74 100%)",
    color: "#fff",
    padding: { xs: "10px 12px", sm: "14px 20px", md: "16px 24px" },
    borderTopLeftRadius: { xs: "6px", sm: "8px" },
    borderTopRightRadius: { xs: "6px", sm: "8px" },
  },
  mainBox: {
    mt: 3,
    px: { xs: "13px", sm: "20px", md: "30px", lg: "50px" },
    pb: { xs: "13px", sm: "15px", md: "20px", lg: "20px" },
  },
  borderBox: { borderRadius: "10px", border: "1px solid #2D2D2D33" },
  fonstyle: {
    fontSize: { xs: "14px", sm: "15px", md: "15px", lg: "16px", xl: "16px" },
    fontWeight: { xs: 500, sm: 500, md: 500, lg: 500 },
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    textTransform: "capitalize",
  },
  select: {
    borderRadius: "50px",
    minWidth: "140px",
    width: "100%",
    "& .MuiOutlinedInput-notchedOutline": { border: "1px solid black" },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: "black" },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "black" },
    "& .MuiSelect-select": { paddingY: "7px", fontSize: "16px", color: "#000" },
  },
  dateInput: {
    width: "100%",
    minWidth: "120px",
    padding: "7px 10px",
    borderRadius: "50px",
    border: "1px solid black",
    fontSize: "16px",
    color: "#000",
    backgroundColor: "white",
    outline: "none",
    boxSizing: "border-box",
  },
  buttonContainer: {
    display: "flex",
    flexDirection: "row",
    gap: { xs: 1, sm: 1, md: 1.5, lg: 2 },
    justifyContent: {
      xs: "center",
      sm: "center",
      md: "center",
      lg: "flex-end",
    },
    alignItems: "center",
    width: "100%",
    minWidth: { xs: "120px", sm: "120px", md: "140px", lg: "160px" },
    flexWrap: "nowrap",
    padding: { xs: "3px 0", sm: "3px 0", md: "4px 0", lg: "4px 0" },
  },
  actionButton: {
    bgcolor: "#89652A",
    color: "white",
    "&:hover": { bgcolor: "#6b4e1f" },
    fontSize: { xs: "11px", sm: "11px", md: "12px", lg: "13px" },
    height: "38px",
    width: { xs: "36px", sm: "36px", md: "42px", lg: "44px" },
    minWidth: "36px",
    borderRadius: "10px",
    textTransform: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontWeight: 500,
    transition: "all 0.2s ease",
    margin: "0 2px",
    "& .MuiButton-startIcon": {
      margin: 0,
      marginRight: "0px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    },
    "& .MuiSvgIcon-root": {
      fontSize: { xs: "16px", sm: "16px", md: "18px", lg: "20px" },
    },
  },
  uploadBox: {
    border: "2px dashed #B69D74",
    borderRadius: "12px",
    p: 2,
    textAlign: "center",
    mb: 2,
    cursor: "pointer",
    bgcolor: "rgba(182, 157, 116, 0.05)",
  },
  imageGridContainer: {
    display: "grid",
    gridTemplateColumns: { xs: "repeat(2, 1fr)", sm: "repeat(3, 1fr)" },
    gap: 2,
    mb: 3,
  },
  imageGridItem: {
    position: "relative",
    borderRadius: "12px",
    overflow: "hidden",
    aspectRatio: "1/1",
    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
  },
  gridImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  removeImageButton: {
    position: "absolute",
    top: 8,
    right: 8,
    bgcolor: "rgba(0,0,0,0.6)",
    width: 28,
    height: 28,
    "&:hover": { bgcolor: "rgba(0,0,0,0.8)" },
  },
};

